import { Alert, Button, Card, Dropdown, Input, Space, Tag, Typography, message } from "antd";
import type { ShippingRequirementPlat, ShippingRequirementPlatVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, Dialog as ZhDialog, Grid as ZhGrid, GridItem as ZhGridItem } from "zhihao-ui-react";

import request, { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import type { OperationHistory } from "@/entity/logistics/operationHistory";
import { SHARE_FROM_PAGE, SHARE_TYPE } from "@/meta/logistics/emptyShipInfo";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import { FREIGHT_RATE_TYPE, MARITIME_AFFAIRS_FEE, PLAT_SHIP_NEED_STATE } from "@/meta/logistics/shipNeed";
import { LINE_PAYMENT_METHOD } from "@/meta/expenseManage/shippingDeposit";
import { SHIP_WAY_BILL_TYPE } from "@/meta/logistics/waybill";
import { CANCEL_TYPE, DATE_SOURCE_TYPE, DEMAND_LEVEL, DRAINAGE_CHANNEL, FIND_SHIP_TYPE } from "@zhihaoscm/meta";

export type PlatformShipNeedDetailData = Omit<Partial<ShippingRequirementPlatVo>, "plat"> & {
    plat: Partial<ShippingRequirementPlat> & { id: string | number };
};

export interface PlatformShipNeedDetailPageProps {
    loadNeed?: (id: string) => Promise<PlatformShipNeedDetailData>;
    loadOperationHistory?: (bizNo: string) => Promise<OperationHistory[]>;
    generateShareQrCode?: (params: PlatformShipNeedShareRequest) => Promise<{ qrCodeUrl: string; trackId?: string }>;
}

export interface PlatformShipNeedShareRequest {
    type: number;
    bizNo: string;
    shareType: number;
}

/** 复用 Vue 平台船运需求详情接口，保持历史详情 VO 路径。 */
export async function loadPlatformShipNeedDetail(id: string): Promise<PlatformShipNeedDetailData> {
    const response = await shippingRequestHttp.service.get<PlatformShipNeedDetailData>(
        `/shipping/plat/find/vo/${encodeURIComponent(id)}`
    );
    return response.data;
}

/** 复用旧 Vue 平台船运需求分享二维码接口，并解析文件服务返回的图片地址。 */
export async function generatePlatformShipNeedShareQrCode(params: PlatformShipNeedShareRequest) {
    const response = await shippingRequestHttp.service.post<{ fileId: number; trackId?: string }>(
        "/share-qrcode/wx-qrcode",
        params
    );
    const fileResponse = await request.service.get<{ path: string }>(`/dataService/admin/file/${response.data.fileId}`);
    return { qrCodeUrl: fileResponse.data.path, trackId: response.data.trackId };
}

/** 复用旧 Vue 操作历史查询接口，固定船运需求发布模块值 3。 */
export async function loadPlatformShipNeedOperationHistory(bizNo: string): Promise<OperationHistory[]> {
    const response = await request.service.get<OperationHistory[]>("/admin/operationHistory/findByModule", {
        params: {
            bizNo,
            module: Operation_History_Enum.LOGISTICS_MANAGEMENT_SHIPPING_DEMAND_PUBLISH
        }
    });
    return response.data;
}

function text(value: unknown): string {
    return value == null || value === "" ? DEFAULT_VALUE : String(value);
}

function dateText(value: unknown): string {
    return typeof value === "string" && value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function amountText(value: unknown): string {
    return typeof value === "number" ? value.toLocaleString("zh-CN") : text(value);
}

function enumText(meta: { getIntroByCode: (code?: number) => string | null }, value?: number): string {
    return meta.getIntroByCode(value) || DEFAULT_VALUE;
}

/** 对照旧 Vue tonOfCargo，保留“随船装”和区间两种展示。 */
function shipTonDemandText(plat: PlatformShipNeedDetailData["plat"]): string {
    if (plat.shipTonDemand) return "随船装";
    if (plat.tonMin == null && plat.tonMax == null) return DEFAULT_VALUE;
    return `${amountText(plat.tonMin)} - ${amountText(plat.tonMax)}`;
}

/** 复刻旧 Vue 操作历史的动作、时间和可展开变更详情。 */
function PlatformShipNeedHistory({ logs }: { logs: OperationHistory[] }) {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    if (!logs.length) return <Typography.Text type="secondary">暂无操作记录</Typography.Text>;

    return (
        <ol aria-label="平台船运需求操作记录" className="m-0 list-none p-0">
            {logs.map((log, index) => {
                const key = String(log.id || index);
                const changes = Array.isArray(log.operationContent) ? log.operationContent : [];
                const isExpanded = Boolean(expanded[key]);

                return (
                    <li key={key} className="mb-3">
                        <div className="flex items-start gap-2">
                            <span>{index + 1}.</span>
                            <span>{dateText(log.createdTime)}</span>
                            <span>{`${log.operationAction || DEFAULT_VALUE}。`}</span>
                            {changes.length ? (
                                <Button
                                    aria-label={isExpanded ? "收起操作详情" : "展开操作详情"}
                                    size="small"
                                    type="link"
                                    onClick={() => setExpanded(current => ({ ...current, [key]: !isExpanded }))}
                                >
                                    {isExpanded ? "收起" : "详情"}
                                </Button>
                            ) : null}
                        </div>
                        {isExpanded ? (
                            <div className="ml-6 mt-2">
                                {changes.map((change, changeIndex) => (
                                    <div key={`${key}-${changeIndex}`}>
                                        修改了 <strong>{change.key || DEFAULT_VALUE}</strong>，旧值为“
                                        {change.oldValue || DEFAULT_VALUE}”，新值为“{change.newValue || DEFAULT_VALUE}
                                        ”。
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </li>
                );
            })}
        </ol>
    );
}

/** 平台船运需求详情，覆盖 Vue 货物、运输、来源和历史操作入口。 */
export function PlatformShipNeedDetailPage({
    loadNeed = loadPlatformShipNeedDetail,
    loadOperationHistory = loadPlatformShipNeedOperationHistory,
    generateShareQrCode = generatePlatformShipNeedShareQrCode
}: PlatformShipNeedDetailPageProps) {
    const { id: routeId } = useParams();
    const location = useLocation();
    // 直接嵌入组件时没有 Route 参数，兼容从历史路径读取详情编号。
    const id = routeId || decodeURIComponent(location.pathname.split("/").filter(Boolean).pop() || "");
    const navigate = useNavigate();
    const [data, setData] = useState<PlatformShipNeedDetailData>();
    const [operationHistory, setOperationHistory] = useState<OperationHistory[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmShipId, setConfirmShipId] = useState("");
    const [cancelVisible, setCancelVisible] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [shareMode, setShareMode] = useState<"wechat" | "poster">();
    const [shareQrCodeUrl, setShareQrCodeUrl] = useState("");

    useEffect(() => {
        if (!id) return;
        let disposed = false;
        void loadNeed(id)
            .then(result => {
                if (disposed) return;
                setData(result);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setData(undefined);
                setErrorMessage("船运需求详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadNeed]);

    useEffect(() => {
        const bizNo = data?.plat?.id;
        if (!bizNo) {
            setOperationHistory([]);
            return;
        }

        let disposed = false;
        setOperationHistory([]);
        // Vue 详情页使用“船运需求发布”模块查询操作历史，保持接口参数契约不变。
        void loadOperationHistory(String(bizNo))
            .then(result => {
                if (!disposed) setOperationHistory(result || []);
            })
            .catch(() => {
                if (!disposed) setOperationHistory([]);
            });

        return () => {
            disposed = true;
        };
    }, [data, loadOperationHistory]);

    if (errorMessage) return <Alert message={errorMessage} role="alert" type="error" />;
    if (!data) return <section aria-label="platform-ship-need-detail-page" />;

    const plat = data.plat;
    const state = Number(plat.state);
    const canEdit =
        (state === PLAT_SHIP_NEED_STATE.PUBLISH.code && !data.accepts?.length) ||
        state === PLAT_SHIP_NEED_STATE.TO_BE_PUBLISH.code;
    const canAssign = state !== PLAT_SHIP_NEED_STATE.END.code && state !== PLAT_SHIP_NEED_STATE.CANCELED.code;
    const canCancel =
        state !== PLAT_SHIP_NEED_STATE.END.code &&
        state !== PLAT_SHIP_NEED_STATE.TO_BE_CONFIRMED.code &&
        state !== PLAT_SHIP_NEED_STATE.CANCELED.code;

    /** 按旧 Vue shareWeiXin/sharePosterCard 传递来源、业务编号和分享类型。 */
    const requestShare = async (shareType: number) => {
        try {
            const result = await generateShareQrCode({
                type: SHARE_FROM_PAGE.SHIPPING_REQUIREMENT_PLAT.code,
                bizNo: String(plat.id),
                shareType
            });
            setShareQrCodeUrl(result.qrCodeUrl);
            setShareMode(shareType === SHARE_TYPE.WX_FRIEND_SHARE.code ? "wechat" : "poster");
            setErrorMessage("");
        } catch {
            setErrorMessage("分享二维码加载失败");
        }
    };

    /** 按 Vue 确认弹窗契约提交船舶编号，成功后重新加载详情。 */
    const submitConfirm = async () => {
        setErrorMessage("");
        if (!confirmShipId.trim()) {
            setErrorMessage("请选择船舶");
            return;
        }
        try {
            await shippingRequestHttp.service.put(
                `/shipping/plat/confirm-ship/${encodeURIComponent(String(plat.id))}`,
                {
                    shipId: confirmShipId.trim()
                }
            );
            setConfirmVisible(false);
            setConfirmShipId("");
            message.success("已确认");
            await loadNeed(String(plat.id)).then(setData);
        } catch {
            setErrorMessage("确认失败");
        }
    };

    /** 按 Vue 取消弹窗契约提交原因，失败时保留弹窗便于重试。 */
    const submitCancel = async () => {
        setErrorMessage("");
        if (!cancelReason.trim()) {
            setErrorMessage("请输入取消原因");
            return;
        }
        try {
            await shippingRequestHttp.service.get(`/shipping/plat/cancel/${encodeURIComponent(String(plat.id))}`, {
                params: { reason: cancelReason.trim() }
            });
            setCancelVisible(false);
            setCancelReason("");
            message.success("已取消");
            await loadNeed(String(plat.id)).then(setData);
        } catch {
            setErrorMessage("取消失败");
        }
    };

    return (
        <section aria-label="platform-ship-need-detail-page" className="flex min-h-full flex-col gap-4">
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            <Card>
                <Space wrap>
                    <Typography.Title className="mb-0" level={4}>
                        船运需求发布详情
                    </Typography.Title>
                    <span>
                        编号 <span>{text(plat.id)}</span>
                    </span>
                    <span>需求来源 {enumText(DATE_SOURCE_TYPE, plat.dataSource)}</span>
                    <span>需求等级 {enumText(DEMAND_LEVEL, plat.demandLevel)}</span>
                    <Tag>{enumText(PLAT_SHIP_NEED_STATE, state)}</Tag>
                    {state === PLAT_SHIP_NEED_STATE.PUBLISH.code ? (
                        <Dropdown
                            menu={{
                                items: [
                                    { key: String(SHARE_TYPE.WX_FRIEND_SHARE.code), label: "微信好友" },
                                    { key: String(SHARE_TYPE.POSTER_SHARE.code), label: "生成海报" }
                                ],
                                onClick: ({ key }) => void requestShare(Number(key))
                            }}
                            trigger={["click"]}
                        >
                            <Button aria-label="分享">分享</Button>
                        </Dropdown>
                    ) : null}
                </Space>
                <ZhGrid className="mt-4" cols={3} gap={[30, 20]}>
                    <ZhBaseItem label="船务专员" value={text(plat.handlerName)} />
                    <ZhBaseItem label="创建时间" value={dateText(plat.createdTime)} />
                    <ZhBaseItem label="取消类型" value={enumText(CANCEL_TYPE, plat.cancelType)} />
                </ZhGrid>
                {state === PLAT_SHIP_NEED_STATE.CANCELED.code ? (
                    <ZhGrid className="mt-4" cols={3} gap={[30, 20]}>
                        <ZhBaseItem label="取消时间" value={dateText(plat.cancelTime)} />
                        <ZhBaseItem label="取消原因" value={text(plat.cancelReasonContent)} />
                        {CANCEL_TYPE.AGENT_CANCEL.match(plat.cancelType) ? (
                            <ZhBaseItem label="其他补充说明" value={text(plat.ownerCancelRemark)} />
                        ) : null}
                    </ZhGrid>
                ) : null}
            </Card>
            <Card title="货物信息">
                <ZhGrid cols={4} gap={[30, 20]}>
                    <ZhBaseItem label="货主" value={text(plat.ownerEnterprise?.name)} />
                    <ZhBaseItem label="货物类型" value={text(plat.goodsType)} />
                    <ZhBaseItem label="联系人" value={text(plat.contact)} />
                    <ZhBaseItem label="手机号" value={text(plat.mobile)} />
                    <ZhBaseItem label="船型" value={enumText(FIND_SHIP_TYPE, plat.shipType)} />
                    <ZhBaseItem label="排水槽" value={enumText(DRAINAGE_CHANNEL, plat.drainageChannel ?? undefined)} />
                    <ZhBaseItem label="运货吨数" value={`${amountText(plat.freightTons)} 吨`} />
                    <ZhBaseItem label="货主补充约定" value={text(plat.replenishAppoint)} />
                </ZhGrid>
            </Card>
            <Card title="运输信息">
                <ZhGrid cols={4} gap={[30, 20]}>
                    <ZhBaseItem label="始发地" value={text(plat.sourcePortName)} />
                    <ZhBaseItem label="目的地" value={text(plat.destinationPortName)} />
                    {/* 旧 Vue 页的空位占两列，后续内容以 GridItem 明确控制跨列范围。 */}
                    <ZhGridItem span={2} />
                    <ZhGridItem className="freight-info" span={4}>
                        <ZhBaseItem
                            label="意向运价"
                            value={
                                plat.unitPriceType === FREIGHT_RATE_TYPE.CALL_FOR_PRICE.code
                                    ? FREIGHT_RATE_TYPE.CALL_FOR_PRICE.intro
                                    : `${amountText(plat.unitPrice)} 元/吨`
                            }
                        />
                    </ZhGridItem>
                    <ZhGridItem span={1}>
                        <ZhBaseItem
                            label="预期装载日期"
                            value={`${dateText(plat.loadDate)} +${text(plat.loadDays)}天`}
                        />
                    </ZhGridItem>
                    <ZhGridItem span={3}>
                        <ZhBaseItem label="两港装卸天数" value={text(plat.loadUnloadDays)} />
                    </ZhGridItem>
                    <ZhBaseItem label="船舶吨位要求" value={shipTonDemandText(plat)} />
                    <ZhBaseItem
                        label="可接受滞期费"
                        value={
                            SHIP_WAY_BILL_TYPE.SUPERVISION.match(plat.serviceType)
                                ? `${amountText(plat.demurrageFee)} 元/吨/天`
                                : DEFAULT_VALUE
                        }
                    />
                    <ZhBaseItem
                        label="海事费用"
                        value={
                            SHIP_WAY_BILL_TYPE.SUPERVISION.match(plat.serviceType)
                                ? enumText(MARITIME_AFFAIRS_FEE, plat.maritimeAffairsFee)
                                : DEFAULT_VALUE
                        }
                    />
                    <ZhBaseItem label="船运费用支付方式" value={enumText(LINE_PAYMENT_METHOD, plat.payType)} />
                    <ZhGridItem span={4}>
                        <ZhBaseItem label="特殊说明" value={text(plat.specialRemark)} />
                    </ZhGridItem>
                </ZhGrid>
            </Card>
            <Card title="历史记录">
                <PlatformShipNeedHistory logs={operationHistory} />
            </Card>
            <Card>
                <Space wrap>
                    {canAssign ? <Button aria-label="变更指派">变更指派</Button> : null}
                    {canEdit ? <Button aria-label="修改">修改</Button> : null}
                    {state === PLAT_SHIP_NEED_STATE.TO_BE_CONFIRMED.code ? (
                        <Button aria-label="确认" onClick={() => setConfirmVisible(true)}>
                            确认
                        </Button>
                    ) : null}
                    {canCancel ? (
                        <Button aria-label="取消" onClick={() => setCancelVisible(true)}>
                            取消
                        </Button>
                    ) : null}
                    {state !== PLAT_SHIP_NEED_STATE.TO_BE_PUBLISH.code ? (
                        <Button aria-label="再来一单">再来一单</Button>
                    ) : null}
                    <Button aria-label="返回" type="primary" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </Space>
            </Card>
            <ZhDialog
                title="确认船舶"
                open={confirmVisible}
                width={600}
                onCancel={() => {
                    setConfirmVisible(false);
                    setConfirmShipId("");
                }}
                footer={
                    <Space>
                        <Button
                            onClick={() => {
                                setConfirmVisible(false);
                                setConfirmShipId("");
                            }}
                        >
                            取消
                        </Button>
                        <Button type="primary" onClick={() => void submitConfirm()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <Typography.Text>请输入平台上已注册的船舶编号</Typography.Text>
                <Input
                    aria-label="船舶编号"
                    value={confirmShipId}
                    onChange={event => setConfirmShipId(event.target.value)}
                />
            </ZhDialog>
            <ZhDialog
                title="取消"
                open={cancelVisible}
                width={400}
                onCancel={() => {
                    setCancelVisible(false);
                    setCancelReason("");
                }}
                footer={
                    <Space>
                        <Button
                            onClick={() => {
                                setCancelVisible(false);
                                setCancelReason("");
                            }}
                        >
                            取消
                        </Button>
                        <Button type="primary" onClick={() => void submitCancel()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <Typography.Text>是否取消，取消后将不能进行任何操作</Typography.Text>
                <Input.TextArea
                    aria-label="取消原因"
                    maxLength={50}
                    value={cancelReason}
                    onChange={event => setCancelReason(event.target.value)}
                />
            </ZhDialog>
            <ZhDialog
                open={Boolean(shareMode)}
                title={shareMode === "poster" ? "海报预览" : "提示"}
                width={400}
                onCancel={() => setShareMode(undefined)}
                footer={<Button onClick={() => setShareMode(undefined)}>取消</Button>}
            >
                <Typography.Paragraph>
                    {shareMode === "poster" ? "使用微信扫码进入小程序分享海报" : "使用微信扫码进入小程序分享"}
                </Typography.Paragraph>
                <div className="flex justify-center">
                    <img alt="分享二维码" className="h-48 w-48" src={shareQrCodeUrl} />
                </div>
            </ZhDialog>
        </section>
    );
}

export default PlatformShipNeedDetailPage;
